import { useCallback, useMemo, useState } from "react";

import {
  useCreatePaymentRemittanceBatch,
  useFinalizePaymentRemittanceBatch,
} from "@/lib/api/paymentRemittancePdfs";
import { UploadPresignedFile } from "@/lib/file";

import type { FileEntry, Group } from "../state/types";
import { runAllUploads } from "./runAllUploads";
import type {
  UploadGroupResult,
  UploadPresignedFileResult,
  UploadStatus,
  UploaderDeps,
} from "./types";

type StartAllArgs = {
  groups: Group[];
  files: Record<string, FileEntry>;
  period_date: string;
};

export type UseWizardUploaderResult = {
  /**
   * Per-group upload status. Missing keys mean the worker pool has
   * not picked the group up yet — `PhaseUploading` treats absent keys
   * as `{ kind: "idle" }`.
   */
  statusByGroupId: Map<string, UploadStatus>;
  /**
   * Kick off the upload pool. Returns the per-group terminal result
   * map (mirrors `runAllUploads`).
   */
  startAll: (args: StartAllArgs) => Promise<Map<string, UploadGroupResult>>;
};

/**
 * Hook boundary between the React-Query / React-hook world and the
 * pure async upload helpers.
 *
 * - Calls the two upload-related React-Query hooks at the top so the
 *   rules of hooks are satisfied.
 * - Wraps `UploadPresignedFile` into the typed `UploadPresignedFileResult`
 *   shape that the pure helpers consume.
 * - Owns the `statusByGroupId` state. Worker callbacks update it via
 *   the functional updater so concurrent writes from the pool never
 *   stomp on each other's earlier updates.
 * - Exposes `startAll(...)`, a stable-identity callback that calls
 *   `runAllUploads(...)` with the deps and status forwarder.
 */
export function useWizardUploader(): UseWizardUploaderResult {
  const createBatch = useCreatePaymentRemittanceBatch();
  const finalizeBatch = useFinalizePaymentRemittanceBatch();

  const [statusByGroupId, setStatusByGroupId] = useState<
    Map<string, UploadStatus>
  >(() => new Map<string, UploadStatus>());

  // Wrap `UploadPresignedFile` so its loose return shape is captured
  // by the typed `UploadPresignedFileResult` contract the pure helpers
  // expect.
  const uploadPresignedFile = useCallback(
    async (
      url: string,
      file: File,
      contentType: string,
    ): Promise<UploadPresignedFileResult> => {
      const raw = await UploadPresignedFile(url, file, contentType);
      const data = (raw.data ?? {}) as {
        status?: number;
        error?: unknown;
      };
      return {
        success: raw.success,
        data: {
          status: typeof data.status === "number" ? data.status : undefined,
          error: data.error,
        },
      };
    },
    [],
  );

  const deps: UploaderDeps = useMemo(
    () => ({
      createBatch,
      finalizeBatch,
      uploadPresignedFile,
    }),
    [createBatch, finalizeBatch, uploadPresignedFile],
  );

  const handleGroupStatus = useCallback(
    (groupId: string, status: UploadStatus) => {
      // Functional updater: workers fire concurrently, so reading
      // `statusByGroupId` from closure would stomp earlier updates.
      setStatusByGroupId((prev: Map<string, UploadStatus>) => {
        const next = new Map(prev);
        next.set(groupId, status);
        return next;
      });
    },
    [],
  );

  const startAll = useCallback(
    async (args: StartAllArgs): Promise<Map<string, UploadGroupResult>> => {
      // Reset the status map so a fresh run never inherits stale
      // statuses from a previous run.
      setStatusByGroupId(new Map<string, UploadStatus>());
      return runAllUploads({
        groups: args.groups,
        files: args.files,
        period_date: args.period_date,
        deps,
        onGroupStatus: handleGroupStatus,
      });
    },
    [deps, handleGroupStatus],
  );

  return { statusByGroupId, startAll };
}
